import React, { useState, useMemo, useEffect } from "react";
import {
	Box,
	Typography,
	CircularProgress,
	Paper,
	makeStyles,
} from "@material-ui/core";
import Zoom from "@material-ui/core/Zoom";
import { CancelButton, OkButton } from "../../../../../styles/button/styles";
import { CustomModal } from "../../../../../styles/modal/styles";
import { ModalContent } from "../../../../../styles/box/styles";
import CheckboxComponent from "../../../../Common/Checkboxes/CheckboxComponent";
import OutletsDropdown from "../../../../Common/Dropdowns/OutletsDropdown";
import BrandsDropdown from "../../../../Common/Dropdowns/BrandsDropdown";

const useStyles = makeStyles((theme) => ({
	infoCard: {
		padding: theme.spacing(2),
		backgroundColor: "#FAFAFA",
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
	},
	infoLabel: {
		fontSize: "0.875rem",
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
	},
	infoValue: {
		fontSize: "1rem",
		fontWeight: 500,
		marginBottom: theme.spacing(2),
		wordBreak: "break-word",
	},
	lastInfoValue: {
		fontSize: "1rem",
		fontWeight: 500,
		wordBreak: "break-word",
	},
	sectionTitle: {
		fontSize: "1rem",
		fontWeight: 500,
		marginBottom: theme.spacing(2),
	},
	noOutlet: {
		color: theme.palette.text.secondary,
	},
}));

const MessageTemplatesCopyModal = ({
	open,
	onClose,
	onConfirm,
	resources,
	templateDetails,
	accessibleOutlets,
	objectType,
	loading,
	error,
	onOutletChange,
}) => {
	const [targetOutletId, setTargetOutletId] = useState(templateDetails?.outletId || 0);
	const [targetBrandId, setTargetBrandId] = useState(templateDetails?.brandId || 0);
	const [targetIsWaitsOnPremiseDependent, setTargetIsWaitsOnPremiseDependent] = useState(templateDetails?.isWaitsOnPremiseDependent || false);
	const [targetIsWaitsOnPremise, setTargetIsWaitsOnPremise] = useState(templateDetails?.isWaitsOnPremise || false);
	const [disabled, setDisabled] = useState(false);
	const classes = useStyles();

	const handleOutletChange = (e) => {
		const value = parseInt(e.target.value);
		setTargetOutletId(value);

		if (onOutletChange) {
			onOutletChange(value);
		}
	};

	const handleBrandChange = (e) => {
		const value = parseInt(e.target.value);
		setTargetBrandId(value);
	}

	const handleIsWaisOnPremiseDependentChange = (e) => {
		const isChecked = e.target.checked;
		setTargetIsWaitsOnPremiseDependent(isChecked);
		if (!isChecked) {
			setTargetIsWaitsOnPremise(false);
		}
	};

	const handleIsWaisOnPremiseChange = (e) => {
		const isChecked = e.target.checked;
		setTargetIsWaitsOnPremise(isChecked);
	}

	useEffect(() => {
		setDisabled(!open);

		if (open) {
			setTargetOutletId(templateDetails?.outletId || 0);
			setTargetBrandId(templateDetails?.brandId || 0);
			setTargetIsWaitsOnPremiseDependent(templateDetails?.isWaitsOnPremiseDependent || false);
			setTargetIsWaitsOnPremise(templateDetails?.isWaitsOnPremise || false);
		}
	}, [open]);

	const confirmCopy = async () => {
		setDisabled(true);

		await onConfirm({
			targetOutletId,
			targetBrandId,
			targetIsWaitsOnPremiseDependent,
			targetIsWaitsOnPremise,
		});
	};

	const getHelperText = useMemo(() => {
		if (error === "MessageTemplateAlreadyExists" && targetOutletId !== 0) {
			return resources["MessageTemplateAlreadyExistsForThisOutlet"];
		}

		return resources["SelectionOfOutletIsMandatory"];
	}, [error, targetOutletId, resources]);

	return (
		<CustomModal
			open={open}
			onClose={onClose}
			BackdropProps={{ style: { backgroundColor: "#0000004D" } }}
		>
			<Zoom in={open} timeout={350}>
				<ModalContent style={{ transform: open ? "scale(1)" : "scale(0.3)" }}>
					<Box my={2}>
						<Typography variant="h5" align="center">
							{resources["CopyMessageTemplate"]}
						</Typography>
					</Box>

					{templateDetails && (
						<Box>
							<Paper elevation={0} className={classes.infoCard}>
								<Typography className={classes.infoLabel}>
									{resources["UseCaseDescription"]}
								</Typography>
								<Typography className={classes.infoValue}>
									{templateDetails.useCaseDescription ||
										resources["NotAvailable"]}
								</Typography>
							</Paper>
						</Box>
					)}

					<Box>
						<OutletsDropdown
							id="outlet-dropdown-copy"
							resources={resources}
							outlets={accessibleOutlets}
							outlet={targetOutletId}
							labelText={resources["Outlet"]}
							helperText={getHelperText}
							onOutletChange={handleOutletChange}
						/>
						<BrandsDropdown
							id="brand-dropdown-copy"
							resources={resources}
                            brand={targetBrandId}
                            objectType={objectType}
                            onBrandChange={handleBrandChange}
						/>
						<CheckboxComponent
							label={resources["IsWaitsOnPremiseDependent"]}
							id="isWaitsOnPremiseDependent-copy"
							checked={targetIsWaitsOnPremiseDependent}
							onChange={handleIsWaisOnPremiseDependentChange}
						/>
						<CheckboxComponent
							label={resources["IsWaitsOnPremise"]}
							id="isWaitsOnPremise-copy"
							checked={targetIsWaitsOnPremise}
							onChange={handleIsWaisOnPremiseChange}
							disabled={!targetIsWaitsOnPremiseDependent}
						/>
					</Box>

					{loading ? (
						<Box display="flex" justifyContent="center" my={3}>
							<CircularProgress />
						</Box>
					) : (
						<Box display="flex" justifyContent="flex-end" padding={2} mt={1}>
							<CancelButton id="btnCancelCopy" onClick={onClose}>
								{resources["Cancel"]}
							</CancelButton>
							<Box ml={2}>
								<OkButton
									id="btnCopy"
									onClick={confirmCopy}
									disabled={disabled}
								>
									{resources["Copy"]}
								</OkButton>
							</Box>
						</Box>
					)}

					<span className="rodal-close" onClick={onClose}></span>
				</ModalContent>
			</Zoom>
		</CustomModal>
	);
};

export default MessageTemplatesCopyModal;
